/**
 * Projects API service - handles project API calls
 */

import { apiClient } from './client';

// Types
export interface Project {
  id: string;
  organizationId: string;
  name: string;
  key: string;
  description?: string;
  isPublic: boolean;
  status: 'active' | 'archived' | 'deleted';
  icon?: string;
  color?: string;
  taskCounter: number;
  settings: ProjectSettings;
  createdAt: string;
  updatedAt: string;
  // Stats (included in list response)
  memberCount?: number;
  boardCount?: number;
  taskCount?: number;
}

export interface ProjectSettings {
  defaultBoardView: 'kanban' | 'list' | 'timeline' | 'calendar' | 'table';
  enableTimeTracking: boolean;
  enableSubtasks: boolean;
  enableDependencies: boolean;
  enableCustomFields: boolean;
  taskTypes: string[];
  priorities: string[];
}

export interface ProjectMember {
  id: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string;
  };
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string;
}

export interface ProjectStats {
  taskCount: number;
  completedTaskCount: number;
  overdueTaskCount: number;
  memberCount: number;
  boardCount: number;
}

export interface CreateProjectRequest {
  organizationId: string;
  name: string;
  key: string;
  description?: string;
  isPublic?: boolean;
  icon?: string;
  color?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  isPublic?: boolean;
  status?: 'active' | 'archived';
  icon?: string;
  color?: string;
}

export interface AddMemberRequest {
  userId: string;
  role: 'admin' | 'member' | 'viewer';
}

export interface ProjectListResponse {
  items: Project[];
  total: number;
}

/**
 * Projects API methods
 */
export const projectsApi = {
  /**
   * List organization projects
   */
  async list(organizationId: string, options?: { page?: number; pageSize?: number }): Promise<ProjectListResponse> {
    const response = await apiClient.get<any>('/projects', {
      params: { organization_id: organizationId, ...(options?.page ? { page: options.page } : {}), ...(options?.pageSize ? { page_size: options.pageSize } : {}) },
    });
    return {
      items: response.data.items.map(transformProject),
      total: response.data.total,
    };
  },

  /**
   * Get project by ID
   */
  async get(projectId: string): Promise<Project> {
    const response = await apiClient.get<any>(`/projects/${projectId}`);
    return transformProject(response.data);
  },

  /**
   * Create a new project
   */
  async create(data: CreateProjectRequest): Promise<Project> {
    const response = await apiClient.post<any>('/projects', {
      organization_id: data.organizationId,
      name: data.name,
      key: data.key,
      description: data.description,
      is_public: data.isPublic ?? false,
      icon: data.icon,
      color: data.color,
    });
    return transformProject(response.data);
  },

  /**
   * Update a project
   */
  async update(projectId: string, data: UpdateProjectRequest): Promise<Project> {
    const response = await apiClient.patch<any>(`/projects/${projectId}`, {
      name: data.name,
      description: data.description,
      is_public: data.isPublic,
      status: data.status,
      icon: data.icon,
      color: data.color,
    });
    return transformProject(response.data);
  },

  /**
   * Delete (soft) a project
   */
  async delete(projectId: string): Promise<void> {
    await apiClient.delete(`/projects/${projectId}`);
  },

  /**
   * Archive a project
   */
  async archive(projectId: string): Promise<Project> {
    const response = await apiClient.post<any>(`/projects/${projectId}/archive`);
    return transformProject(response.data);
  },

  /**
   * Restore an archived project
   */
  async restore(projectId: string): Promise<Project> {
    const response = await apiClient.post<any>(`/projects/${projectId}/restore`);
    return transformProject(response.data);
  },

  /**
   * Get project statistics
   */
  async getStats(projectId: string): Promise<ProjectStats> {
    const response = await apiClient.get<any>(`/projects/${projectId}/stats`);
    return {
      taskCount: response.data.taskCount ?? response.data.task_count,
      completedTaskCount: response.data.completedTaskCount ?? response.data.completed_task_count,
      overdueTaskCount: response.data.overdueTaskCount ?? response.data.overdue_task_count,
      memberCount: response.data.memberCount ?? response.data.member_count,
      boardCount: response.data.boardCount ?? response.data.board_count,
    };
  },

  /**
   * List project members
   */
  async listMembers(projectId: string): Promise<ProjectMember[]> {
    const response = await apiClient.get<any[]>(`/projects/${projectId}/members`);
    return response.data.map(transformMember);
  },

  /**
   * Add a member to a project
   */
  async addMember(projectId: string, data: AddMemberRequest): Promise<ProjectMember> {
    const response = await apiClient.post<any>(`/projects/${projectId}/members`, {
      user_id: data.userId,
      role: data.role,
    });
    return transformMember(response.data);
  },

  /**
   * Update member role
   */
  async updateMemberRole(
    projectId: string,
    userId: string,
    role: string
  ): Promise<ProjectMember> {
    const response = await apiClient.patch<any>(
      `/projects/${projectId}/members/${userId}`,
      { role }
    );
    return transformMember(response.data);
  },

  /**
   * Remove a member from a project
   */
  async removeMember(projectId: string, userId: string): Promise<void> {
    await apiClient.delete(`/projects/${projectId}/members/${userId}`);
  },

  /**
   * Update project settings
   */
  async updateSettings(
    projectId: string,
    settings: Partial<ProjectSettings>
  ): Promise<ProjectSettings> {
    const response = await apiClient.patch<any>(
      `/projects/${projectId}/settings`,
      transformSettingsToSnakeCase(settings)
    );
    return transformSettings(response.data);
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformProject(data: any): Project {
  return {
    id: data.id,
    organizationId: data.organizationId ?? data.organization_id,
    name: data.name,
    key: data.key,
    description: data.description,
    isPublic: data.isPublic ?? data.is_public,
    status: data.status,
    icon: data.icon,
    color: data.color,
    taskCounter: data.taskCounter ?? data.task_counter,
    settings: transformSettings(data.settings || {}),
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
    memberCount: data.memberCount ?? data.member_count,
    boardCount: data.boardCount ?? data.board_count,
    taskCount: data.taskCount ?? data.task_count,
  };
}

function transformSettings(data: any): ProjectSettings {
  return {
    defaultBoardView: data.defaultBoardView ?? data.default_board_view ?? 'kanban',
    enableTimeTracking: data.enableTimeTracking ?? data.enable_time_tracking ?? true,
    enableSubtasks: data.enableSubtasks ?? data.enable_subtasks ?? true,
    enableDependencies: data.enableDependencies ?? data.enable_dependencies ?? true,
    enableCustomFields: data.enableCustomFields ?? data.enable_custom_fields ?? true,
    taskTypes: data.taskTypes ?? data.task_types ?? ['task', 'bug', 'story', 'epic'],
    priorities: data.priorities || ['urgent', 'high', 'medium', 'low', 'none'],
  };
}

function transformSettingsToSnakeCase(settings: Partial<ProjectSettings>): any {
  const result: any = {};
  if (settings.defaultBoardView !== undefined) {
    result.default_board_view = settings.defaultBoardView;
  }
  if (settings.enableTimeTracking !== undefined) {
    result.enable_time_tracking = settings.enableTimeTracking;
  }
  if (settings.enableSubtasks !== undefined) {
    result.enable_subtasks = settings.enableSubtasks;
  }
  if (settings.enableDependencies !== undefined) {
    result.enable_dependencies = settings.enableDependencies;
  }
  if (settings.enableCustomFields !== undefined) {
    result.enable_custom_fields = settings.enableCustomFields;
  }
  if (settings.taskTypes !== undefined) {
    result.task_types = settings.taskTypes;
  }
  if (settings.priorities !== undefined) {
    result.priorities = settings.priorities;
  }
  return result;
}

function transformMember(data: any): ProjectMember {
  return {
    id: data.id,
    user: {
      id: data.user.id,
      email: data.user.email,
      displayName: data.user.displayName ?? data.user.display_name,
      avatarUrl: data.user.avatarUrl ?? data.user.avatar_url,
    },
    role: data.role,
    joinedAt: data.joinedAt ?? data.joined_at,
  };
}

export default projectsApi;
